import type { ParsedRecipe } from "@/lib/recipes/import-types";
import { supabase } from "@/lib/supabase";
import { useState } from "react";

type ImportState =
  | { status: "idle" }
  | { status: "loading"; stage: string }
  | { status: "success"; data: ParsedRecipe }
  | { status: "error"; message: string };

function friendlyError(raw: string): string {
  const msg = raw.trim();
  if (!msg) return "Something went wrong. Please try again.";
  if (msg.toLowerCase().includes("timeout")) return "Took too long. Check your connection and try again.";
  if (msg.includes("403") || msg.toLowerCase().includes("blocked") || msg.toLowerCase().includes("blocks automated")) return "This site doesn't allow importing. Try entering manually.";
  if (msg.toLowerCase().includes("paywall") || msg.includes("401")) return "This recipe is behind a paywall.";
  if (msg.toLowerCase().includes("not found") || msg.includes("404")) return "Recipe page not found.";
  if (msg.toLowerCase().includes("could not parse") || msg.toLowerCase().includes("couldn't read")) return "Couldn't read this recipe. Try a different link or enter manually.";
  if (msg.toLowerCase().includes("timed out")) return "Request timed out. Check your connection or try again.";
  if (msg.toLowerCase().includes("fetch") && msg.length < 80) return "Failed to load the recipe page. Try again or enter manually.";
  if (msg.length > 20 && !msg.includes("Error") && !msg.includes("ECONNREFUSED")) return msg;
  return "Something went wrong. Please try again.";
}

export function useImportRecipe() {
  const [state, setState] = useState<ImportState>({ status: "idle" });

  async function importRecipe(url: string): Promise<ParsedRecipe | null> {
    setState({ status: "loading", stage: "Fetching recipe..." });
    const t1 = setTimeout(
      () => setState((s) => (s.status === "loading" ? { ...s, stage: "Reading ingredients..." } : s)),
      1500
    );
    const t2 = setTimeout(
      () => setState((s) => (s.status === "loading" ? { ...s, stage: "Almost done..." } : s)),
      3000
    );
    try {
      const { data, error } = await supabase.functions.invoke("parse-recipe", { body: { url } });
      clearTimeout(t1);
      clearTimeout(t2);
      if (data?.error || error) throw new Error("Unable to import from this site");
      const recipe = data?.data as ParsedRecipe;
      setState({ status: "success", data: recipe });
      return recipe;
    } catch (e: unknown) {
      clearTimeout(t1);
      clearTimeout(t2);
      const message = e instanceof Error ? e.message : String(e);
      setState({ status: "error", message: friendlyError(message) });
      return null;
    }
  }

  function reset(): void {
    setState({ status: "idle" });
  }

  return { state, importRecipe, reset };
}
